// routes/index.tsx
import React from 'react';
import { lazy } from 'react';
import { Navigate, RouteObject, useLocation, useRoutes, matchRoutes } from 'react-router-dom';
import { PATH } from './v1/src/utils/constants';
import { GuestGuard } from './commons/components/GuestGuard';
import DeviceGuard from './commons/components/DeviceGuard';
import { AgentVersion } from './commons/services/agent.service';
import { useAuth } from './commons/useAuth';
import { useVersion } from './commons/contexts/VersionContext';
import Loading from './commons/components/Loading';
import { routes as v1RoutesRaw } from './v1/src/routers';
import { routes as v2RoutesRaw } from './v2/src/routers';
import { HeaderProvider as V1HeaderProvider } from './v1/src/components/Header';
import { HeaderProvider as V2HeaderProvider } from './v2/src/components/Header';
import { isPublicSubdomainHost, PUBLIC_DOMAIN_SUFFIX } from './commons/utils/common';

const LoginPage = lazy(() => import('./commons/LoginPage'));
const SendResetMailPage = lazy(() => import('./commons/SendResetMailPage'));
const ResetPasswordPage = lazy(() => import('./commons/ResetPasswordPage'));
const SignupPage = lazy(() => import('./commons/SignupPage'));
const ThankyouPage = lazy(() => import('./commons/ThankyouPage'));
const PublicPage = lazy(() => import('./commons/pages/PublicPage'));
const NotFoundPage = lazy(() => import('./commons/pages/NotFoundPage'));

// Filter out login route from v1 and v2 routes to avoid conflicts
const v1Routes = v1RoutesRaw.filter((route) => route.path !== PATH.LOGIN);
const v2Routes = v2RoutesRaw.filter((route) => route.path !== PATH.LOGIN);

/**
 * Returns a copy of routes with any catch-all (path === '*') removed.
 * Used so matchRoutes only matches "real" routes; invalid paths get no match → 404.
 */
function routesWithoutCatchAll(routes: RouteObject[]): RouteObject[] {
  return routes
    .filter((r) => r.path !== '*')
    .map((r) => {
      if (!r.children?.length) return r;
      const children = routesWithoutCatchAll(r.children);
      return { ...r, children };
    });
}

/** Routes for "is this path valid?" – no catch-all, so invalid paths don't match. */
const v1RoutesForMatch = routesWithoutCatchAll(v1Routes);
const v2RoutesForMatch = routesWithoutCatchAll(v2Routes);

/**
 * Component to render v1 routes
 * Version check is already done in VersionRouter
 * Wraps with v1 HeaderProvider
 */
const V1Routes: React.FC = () => {
  const element = useRoutes(v1Routes);
  return (
    <V1HeaderProvider>
      {element}
    </V1HeaderProvider>
  );
};

/**
 * Component to render v2 routes
 * Version check is already done in VersionRouter
 * Wraps with v2 HeaderProvider
 */
const V2Routes: React.FC = () => {
  const element = useRoutes(v2Routes);
  return (
    <V2HeaderProvider>
      {element}
    </V2HeaderProvider>
  );
};

/**
 * Version-based router: selects v1 or v2 based on user version from VersionProvider (API).
 * Component that routes to v1 or v2 based on user's version
 * Version is managed by VersionProvider and always fetched fresh from API
 * 
 * Flow:
 * 1. Not authenticated → redirect to login, save current location to redirect back after login
 * 2. Authenticated, version loading → show loading
 * 3. Version available → render V1Routes or V2Routes
 *
 * @returns Navigate, Loading, or V1Routes/V2Routes
 */
const VersionRouter: React.FC = () => {
  const { auth } = useAuth();
  const { version, isLoading } = useVersion();
  const location = useLocation();

  if (location.pathname === PATH.THANKYOU) {
    return <Navigate to={PATH.THANKYOU} replace />;
  }

  if (!auth) {
    return <Navigate to={PATH.LOGIN} state={{ from: location }} replace />;
  }

  // Show loading while fetching version (after login or on page reload)
  if (isLoading || !version) {
    return <Loading fullScreen />;
  }

  // If we have version, route accordingly
  // V1Routes and V2Routes will handle their own dashboard routing
  if (version === AgentVersion.Studio) {
    return <V1Routes />;
  }
  return <V2Routes />;
};

/**
 * For non-public hosts: if path does not match any v1/v2 route → NotFoundPage;
 * otherwise wrap VersionRouter with DeviceGuard.
 */
const AppWithVersionRouter: React.FC = () => {
  const { pathname } = useLocation();
  const matchV1 = matchRoutes(v1RoutesForMatch, pathname);
  const matchV2 = matchRoutes(v2RoutesForMatch, pathname);
  if (!matchV1 && !matchV2) {
    return <NotFoundPage />;
  }
  return (
    <DeviceGuard>
      <VersionRouter />
    </DeviceGuard>
  );
};

/** App routes used only when host is NOT a public subdomain (login, dashboard, etc.). */
const appRoutes: RouteObject[] = [
  {
    path: PATH.LOGIN,
    element: (
      <DeviceGuard>
        <GuestGuard>
          <LoginPage />
        </GuestGuard>
      </DeviceGuard>
    ),
  },
  {
    path: PATH.SEND_RESET_MAIL,
    element: (
      <DeviceGuard>
        <GuestGuard>
          <SendResetMailPage />
        </GuestGuard>
      </DeviceGuard>
    ),
  },
  {
    path: PATH.RESET_PASSWORD,
    element: (
      <DeviceGuard>
        <GuestGuard>
          <ResetPasswordPage />
        </GuestGuard>
      </DeviceGuard>
    ),
  },
  {
    path: PATH.SIGNUP,
    element: (
      <DeviceGuard>
        <GuestGuard>
          <SignupPage />
        </GuestGuard>
      </DeviceGuard>
    ),
  },
  {
    path: PATH.THANKYOU,
    element: (
      <DeviceGuard>
        <ThankyouPage />
      </DeviceGuard>
    ),
  },
  {
    path: PATH.NOT_FOUND,
    element: <NotFoundPage />,
  },
  {
    path: '/public',
    element: <PublicPage />,
  },
  {
    path: '/*',
    element: <AppWithVersionRouter />,
  },
];

/**
 * Single entry: checks hostname first. On public subdomain only "/" shows PublicPage,
 * any other path (e.g. /login) shows NotFound. On main domain, delegates to app routes.
 */
const HostAwareRouter: React.FC = () => {
  const { pathname } = useLocation();
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';
  const isPublicHost =
    Boolean(PUBLIC_DOMAIN_SUFFIX) &&
    isPublicSubdomainHost(hostname, PUBLIC_DOMAIN_SUFFIX ?? '');

  const appElement = useRoutes(appRoutes);

  if (isPublicHost) {
    return pathname === '/' || pathname === '' ? <PublicPage /> : <NotFoundPage />;
  }

  return appElement;
};

export const routes: RouteObject[] = [
  {
    path: '/*',
    element: <HostAwareRouter />,
  },
];
